import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { showConfirmationAlert } from "../../utils/confirmationAlert";
import { FaUser, FaBox, FaGavel, FaNewspaper, FaCommentAlt, FaCreditCard, FaHome, FaSignOutAlt } from "react-icons/fa";
import importImage from "../../utils/images/importImage";

const DashboardSidebar = ({ isOpen, isMobile, onClose }) => {
  const logo = importImage("logo-dark.png");
  const { t } = useLanguage();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    showConfirmationAlert({
      message: t("dashboardSideNav.confirmLogout", "Are you sure you want to logout?"),
      onConfirm: () => {
        logout();
        console.log("Logged out successfully");
      },
      onCancel: () => {
        console.log("Logout cancelled");
      },
    });
  };

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && isOpen && !event.target.closest('.dashboard-sidebar')) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, isOpen, onClose]);

  // Determine dashboard type based on URL
  const isBuyerDashboard = location.pathname.startsWith("/buyer-dashboard");
  const isSellerDashboard = location.pathname.startsWith("/seller-dashboard");
  const isAdminDashboard = location.pathname.startsWith("/admin");

  const getIconForPath = (path) => {
    const icons = {
      "/admin/user-list": <FaUser className="me-2" />,
      "/admin/products": <FaBox className="me-2" />,
      "/admin/approved-bids": <FaGavel className="me-2" />,
      "/admin/blogs": <FaNewspaper className="me-2" />,
      "/admin/testimonials": <FaCommentAlt className="me-2" />,
      "/admin/payments": <FaCreditCard className="me-2" />,
      "/buyer-dashboard": <FaHome className="me-2" />,
      "/buyer-dashboard/buyer-bids": <FaGavel className="me-2" />,
      "/seller-dashboard": <FaHome className="me-2" />,
      "/seller-dashboard/add-inventory": <FaBox className="me-2" />,
      "/seller-dashboard/seller-bids": <FaGavel className="me-2" />,
      "/profile": <FaUser className="me-2" />,
      "/role": <FaUser className="me-2" />
    };
    
    return icons[path] || <FaHome className="me-2" />;
  };

  return (
    <div className={`dashboard-sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
      <img
            src={logo}
            className="main-logo"
            data-wow-delay="0.9s"
            style={{ objectFit: "cover", maxWidth:"100px",maxHeight:"100px" }}
            alt="logo"
          /> 
        <h3 className="sidebar-title">
          {isAdminDashboard ? 'Admin Panel' : 
           isBuyerDashboard ? 'Buyer Dashboard' : 
           isSellerDashboard ? 'Seller Dashboard' : 'Dashboard'}
        </h3>
      </div>
      
      <ul className="sidebar-menu-list">
        {isAdminDashboard && (
          <>
            <li>
              <Link
                to="/admin/user-list"
                className={`nav-item nav-link ${
                  location.pathname === "/admin/user-list" ? "active" : ""
                }`}
                onClick={isMobile ? onClose : null}
              >
                {getIconForPath("/admin/user-list")}
                {t("dashboardSideNav.navMenu.adminUserList", "Users")}
              </Link>
            </li>
            <li>
              <Link
                to="/admin/products"
                className={`nav-item nav-link ${
                  location.pathname === "/admin/products" ? "active" : ""
                }`}
                onClick={isMobile ? onClose : null}
              >
                {getIconForPath("/admin/products")}
                {t("dashboardSideNav.navMenu.adminProducts", "Products")}
              </Link>
            </li>
            {/*<li>
              <Link
                to="/admin/approved-bids"
                style={{ pointerEvents: "none" }}
                className={`nav-item nav-link ${
                  location.pathname === "/admin/approved-bids" ? "active" : ""
                }`}
                onClick={isMobile ? onClose : null}
              >
                {getIconForPath("/admin/approved-bids")}
                {t("dashboardSideNav.navMenu.approvedBids", "Approved Bids")}
              </Link>
            </li>*/}
            <li>
              <Link
                to="/admin/enquire"
                className={`nav-item nav-link ${
                  location.pathname === "/admin/enquire"
                    ? "active"
                    : ""
                }`}
                onClick={isMobile ? onClose : null}
              >
                {getIconForPath("/buyer-dashboard/buyer-bids")}
                {t("dashboardSideNav.navMenu.buyerEnquire", "Enquire")}
              </Link>
            </li>
            <li>
              <Link
                to="/admin/blogs"
                className={`nav-item nav-link ${
                  location.pathname === "/admin/blogs" ? "active" : ""
                }`}
                onClick={isMobile ? onClose : null}
              >
                {getIconForPath("/admin/blogs")}
                {t("dashboardSideNav.navMenu.blogs", "Blogs")}
              </Link>
            </li>
            <li>
              <Link
                to="/admin/testimonials"
                className={`nav-item nav-link ${
                  location.pathname === "/admin/testimonials" ? "active" : ""
                }`}
                onClick={isMobile ? onClose : null}
              >
                {getIconForPath("/admin/testimonials")}
                {t("dashboardSideNav.navMenu.adminTestimonial", "Testimonials")}
              </Link>
            </li>
         {/*   <li>
              <Link
                to="/admin/payments"
                style={{ pointerEvents: "none" }}
                className={`nav-item nav-link ${
                  location.pathname === "/admin/payments" ? "active" : ""
                }`}
                onClick={isMobile ? onClose : null}
              >
                {getIconForPath("/admin/payments")}
                {t("dashboardSideNav.navMenu.payments", "Payments")}
              </Link>
            </li>*/}
          </>
        )}
        
        {/* Buyer Dashboard Links */}
        {isBuyerDashboard && (
          <>
            <li>
              <Link
                to="/buyer-dashboard"
                className={`nav-item nav-link ${
                  location.pathname === "/buyer-dashboard" ? "active" : ""
                }`}
                onClick={isMobile ? onClose : null}
              >
                {getIconForPath("/buyer-dashboard")}
                {t("dashboardSideNav.navMenu.buyerDashboard", "Dashboard")}
              </Link>
            </li>
            <li>
              <Link
                to="/buyer-dashboard/enquire"
                className={`nav-item nav-link ${
                  location.pathname === "/buyer-dashboard/enquire"
                    ? "active"
                    : ""
                }`}
                onClick={isMobile ? onClose : null}
              >
                {getIconForPath("/buyer-dashboard/buyer-bids")}
                {t("dashboardSideNav.navMenu.buyerEnquire", "Enquire")}
              </Link>
            </li>
            <li>
            <Link
              to="/buyer-dashboard/profile"
              className={`nav-item nav-link ${
                location.pathname === "/buyer-dashboard/profile" ? "active" : ""
              }`}
              onClick={isMobile ? onClose : null}
            >
              {getIconForPath("/profile")}
              {t("dashboardSideNav.navMenu.profile", "Profile")}
            </Link>
          </li>
          </>
        )}

        {/* Seller Dashboard Links */}
        {isSellerDashboard && (
          <>
            <li>
              <Link
                to="/seller-dashboard"
                className={`nav-item nav-link ${
                  location.pathname === "/seller-dashboard" ? "active" : ""
                }`}
                onClick={isMobile ? onClose : null}
              >
                {getIconForPath("/seller-dashboard")}
                {t("dashboardSideNav.navMenu.sellerDashboard", "Dashboard")}
              </Link>
            </li>
            <li>
              <Link
                to="/seller-dashboard/add-inventory"
                className={`nav-item nav-link ${
                  location.pathname === "/seller-dashboard/manage-products"
                    ? "active"
                    : ""
                }`}
                onClick={isMobile ? onClose : null}
              >
                {getIconForPath("/seller-dashboard/add-inventory")}
                {t("dashboardSideNav.navMenu.addInventory", "Add Inventory")}
              </Link>
            </li>
            <li>
              <Link
                to="/seller-dashboard/enquire"
                className={`nav-item nav-link ${
                  location.pathname === "/seller-dashboard/enquire"
                    ? "active"
                    : ""
                }`}
                onClick={isMobile ? onClose : null}
              >
                {getIconForPath("/seller-dashboard/seller-bids")}
                {t("dashboardSideNav.navMenu.sellerEnquire", "Enquire")}
              </Link>
            </li>
            <li>
            <Link
              to="/seller-dashboard/profile"
              className={`nav-item nav-link ${
                location.pathname === "/seller-dashboard/profile" ? "active" : ""
              }`}
              onClick={isMobile ? onClose : null}
            >
              {getIconForPath("/profile")}
              {t("dashboardSideNav.navMenu.profile", "Profile")}
            </Link>
          </li>
          </>
        )}

        {/* Role Link - Show only if not on Seller or Buyer Dashboard */}
        {!isSellerDashboard && !isBuyerDashboard && !isAdminDashboard && (
          <li>
            <Link
              to="/role"
              className={`nav-item nav-link ${
                location.pathname === "/role" ? "active" : ""
              }`}
              onClick={isMobile ? onClose : null}
            >
              {getIconForPath("/role")}
              {t("dashboardSideNav.navMenu.role", "Role")}
            </Link>
          </li>
        )}

        {/* Common Links */}
        {!isAdminDashboard && (
          <></>
          // <li>
          //   <Link
          //     to="/profile"
          //     className={`nav-item nav-link ${
          //       location.pathname === "/profile" ? "active" : ""
          //     }`}
          //     onClick={isMobile ? onClose : null}
          //   >
          //     {getIconForPath("/profile")}
          //     {t("dashboardSideNav.navMenu.profile", "Profile")}
          //   </Link>
          // </li>
        )}

        {/* Logout Link */}
        <li className="mt-auto">
          <Link
            to=""
            className="nav-item nav-link logout-link"
            onClick={(e) => {
              e.preventDefault();
              handleLogout();
              if (isMobile) onClose();
            }}
          >
            <FaSignOutAlt className="me-2" />
            {t("dashboardSideNav.navMenu.logout", "Logout")}
          </Link>
        </li>
      </ul>
    </div>
  );
};

export default DashboardSidebar;